/* eslint-disable max-lines */
/* eslint-disable complexity */
import { Injectable } from '@angular/core';
import { ResetPasswordContent } from '@com-delta-idp/mnx-login-ui';
import {
  ANALYTICS_EVENTS,
  MFA_ACCOUNT_RECOVERY_FAILED
} from '@com-delta-idp/mnx-multi-factor-authentication';
import {
  ERROR_MSG_SEC_CP_CHALLENGE,
  OauthAEMConfig
} from '@com-delta-idp/mnx-oauth';
import { forkJoin, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { nameConfig, SkyMilesNumberValidator } from '../../../validator';
import {
  MFA_FLOW_ACCOUNT_RECOVERY_FIRST_SCREEN,
  SKYMILES_NUMBER_LABEL
} from '../../constants/login-constant';
import { ErrorMessage, LoginModalContent } from '../../models/aem';
import { ResetPasswordSupportFacade } from './reset-password-support.facade';

@Injectable({
  providedIn: 'root'
})
export class ResetPasswordFacade extends ResetPasswordSupportFacade {
  aemContent!: LoginModalContent;
  unsubscribe: Subject<void> = new Subject<void>();
  isResetPassword = true;

  constructor() {
    super();
  }

  setRegex(authConfig: OauthAEMConfig): void {
    nameConfig.firstName = authConfig.FIRST_NAME_REGEX;
    nameConfig.middleName = authConfig.MIDDLE_NAME_REGEX;
    nameConfig.lastName = authConfig.LAST_NAME_REGEX;
  }

  getResetPasswordPageContent() {
    const storedUserId = this.loginModalService.lastEnteredUserId;
    forkJoin([
      this.contentService.getAEMContent(),
      this.pingOauthService.authConfigJson
    ]).subscribe(([response, authConfig]) => {
      this.aemContent = response;
      this.setRegex(authConfig);
      const content = this.resetPasswordadapter.getResetPasswordContent(
        this.aemContent,
        storedUserId
      );

      if (authConfig?.MFA_FLOW) {
        content.formAttributes.isMfa = true;
        content.formGroup.get('userId')?.setValidators(SkyMilesNumberValidator);
        content.pageContent.userIdLabel =
          this.aemContent.resetPassword.mfaUserIdLabel || SKYMILES_NUMBER_LABEL;
        content.pageContent.errors.userIdErrorMessage =
          this.resetPasswordhelperService.getErrorMessageSkymilesNo(
            this.aemContent.errors
          );
        this.loginOAuthParams = {
          oauthConfig: authConfig,
          pkceCode: this.pkceCode
        };
        this.displayMfaResetPasswordErrorMessage(content);
        this.selfServicePasswordResetWrapper
          .selfServiceResetPasswordFlow(
            this.loginOAuthParams,
            'INITIATE_RESET_PASSWORD'
          )
          .pipe(takeUntil(this.unsubscribe))
          .subscribe();
      }
      this.resetPasswordContent$.next(content);
    });
  }

  onResetPasswordFormSubmit(submit = false) {
    if (submit && this.validateForm()) {
      return;
    }

    this.setActionButtonDisable(true);

    if (this.loginOAuthParams?.oauthConfig?.MFA_FLOW) {
      const formValues = this.getResetPasswordContent().formGroup.getRawValue();
      const userId = formValues.userId?.value?.trim();

      this.selfServicePasswordResetWrapper
        .selfServiceResetPasswordFlow(
          this.loginOAuthParams,
          'SUBMIT_SKYMILES_NUMBER',
          userId
        )
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((response) => {
          if (typeof response !== 'string' && response && response.status) {
            if (response.status === 'AUTHENTICATION_REQUIRED') {
              this.analytics.dispatchCustomEvent(
                ANALYTICS_EVENTS.RESET_PASSWORD_MFA_FLOW,
                true
              );
              window.location.replace(
                `${MFA_FLOW_ACCOUNT_RECOVERY_FIRST_SCREEN}?flowId=${response.id}`
              );
            } else {
              this.onResetPasswordFormSubmitBff();
              this.analytics.dispatchCustomEvent(
                ANALYTICS_EVENTS.RESET_PASSWORD_BFF_FLOW,
                true
              );
            }
          } else if (
            typeof response === 'string' &&
            response === ERROR_MSG_SEC_CP_CHALLENGE
          ) {
            this.loggingService.log(
              'Status Code: 428 - Display Code Challenge: ' + response
            );
          } else {
            this.onResetPasswordFormSubmitBff();
            this.analytics.dispatchCustomEvent(
              ANALYTICS_EVENTS.RESET_PASSWORD_BFF_FLOW,
              true
            );
          }
        });
    } else {
      this.onResetPasswordFormSubmitBff();
      this.analytics.dispatchCustomEvent(
        ANALYTICS_EVENTS.RESET_PASSWORD_BFF_FLOW,
        true
      );
    }
  }

  displayMfaResetPasswordErrorMessage(content: ResetPasswordContent) {
    const urlParams = new URLSearchParams(window.location.search);
    const mfaFailed = urlParams.get('mfaFailed') ?? '';
    if (mfaFailed === 'true') {
      this.contentService
        .getAEMContent()
        .pipe(take(1))
        .subscribe(({ errors }) => {
          errors.errorMessages.forEach((option: ErrorMessage) => {
            if (option['errorId'] === MFA_ACCOUNT_RECOVERY_FAILED) {
              content.hasError = true;
              content.errorMessage = option['errorMessageDescription'];
              content.pageContent.mfaFailed = true;
            }
          });
        });
    }
  }
}
